// src/Dashboard/pages/teacher/SuiviCompetitions/utils/suiviCompetitionsUtils.js

import getCategory from "../../../../../utils/getCategory";
import getWeightCategory from "../../../../../utils/getWeightCategory";

export const PAGE_SIZE = 8;

export const CATE_EVENTS = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
];

export const COMPETITION_AGE_CATEGORY_ORDER = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
  "Inconnu",
];

export const STOPPED_STATUSES = [
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié et Arrêt",
];

/* ============================================================
   URL / PHOTO / IDENTITÉ
   ============================================================ */

export const toAbsolute = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${value}`;
};

export const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

export const getAdherentPhoto = (adherent) => {
  if (adherent?.autorisation_photo === false) {
    return "/img/interdiction.jpg";
  }

  return (
    toAbsolute(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar,
    ) || getDefaultPhoto(adherent?.genre)
  );
};

export const getAdherentName = (adherent) => {
  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

export const getGroupsLabel = (adherent) => {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
};

/* ============================================================
   DATES
   ============================================================ */

export const formatDateFR = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatDateLongFR = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const getBirthYear = (dateNaissance) => {
  if (!dateNaissance) return "";

  const date = new Date(dateNaissance);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return String(date.getFullYear());
};

/* ============================================================
   EVENTS
   ============================================================ */

export const eventIsPast = (event) => {
  if (!event?.start) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(event.start);

  if (Number.isNaN(start.getTime())) return false;

  start.setHours(0, 0, 0, 0);

  return start < today;
};

export const getEventCategories = (event) => {
  return String(event?.cate_event || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const getEventStatusMeta = (event) => {
  const past = eventIsPast(event);

  if (past) {
    return {
      label: "Passée",
      className:
        "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    };
  }

  return {
    label: "À venir",
    className:
      "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60",
  };
};

export const isCoachForEvent = (adherent, enseignants = []) => {
  return (enseignants || []).some(
    (enseignant) => Number(enseignant.id) === Number(adherent?.id),
  );
};

/* ============================================================
   CATÉGORIES ÂGE / POIDS
   ============================================================ */

export const normalizeAgeCategoryForWeight = (ageCategory) => {
  const value = String(ageCategory || "").trim();

  const map = {
    Eveil: "Eveil",

    "Mini-Poussin(e)": "Mini-Poussin",
    "Mini-Poussin": "Mini-Poussin",

    "Poussin(e)": "Poussin",
    Poussin: "Poussin",

    "Benjamin(e)": "Benjamin",
    Benjamin: "Benjamin",

    Minime: "Minime",

    "Cadet(te)": "Cadet",
    Cadet: "Cadet",

    Junior: "Junior",

    Sénior: "Sénior",
    Senior: "Sénior",

    Vétéran: "Vétéran",
    Veteran: "Vétéran",

    Inconnu: "Inconnu",
  };

  return map[value] || value;
};

/**
 * Calcule la catégorie d'âge à partir de la saison.
 * Exemple saison 2025-2026 :
 * - Benjamin(e) = années startYear - 11 à startYear - 10
 */
export const getAgeCategoryBySeason = (dateNaissance, season) => {
  if (!dateNaissance || !season) return "Inconnu";

  const birthYear = Number(getBirthYear(dateNaissance));
  const startYear = Number(String(season).split("-")?.[0]);

  if (!birthYear || !startYear) return "Inconnu";

  if (birthYear >= startYear - 5 && birthYear <= startYear - 3) {
    return "Eveil";
  }

  if (birthYear >= startYear - 7 && birthYear <= startYear - 6) {
    return "Mini-Poussin(e)";
  }

  if (birthYear >= startYear - 9 && birthYear <= startYear - 8) {
    return "Poussin(e)";
  }

  if (birthYear >= startYear - 11 && birthYear <= startYear - 10) {
    return "Benjamin(e)";
  }

  if (birthYear >= startYear - 13 && birthYear <= startYear - 12) {
    return "Minime";
  }

  if (birthYear >= startYear - 16 && birthYear <= startYear - 14) {
    return "Cadet(te)";
  }

  if (birthYear >= startYear - 19 && birthYear <= startYear - 17) {
    return "Junior";
  }

  if (birthYear >= startYear - 29 && birthYear <= startYear - 20) {
    return "Sénior";
  }

  if (birthYear <= startYear - 30) {
    return "Vétéran";
  }

  return "Inconnu";
};

/**
 * Helper central pour toutes les cards compétition.
 * Important :
 * - si une saison est passée, on calcule la catégorie selon cette saison ;
 * - sinon fallback sur adherent.ageCategory puis getCategory().
 */
export const getAdherentCompetitionCategories = (adherent, season = "") => {
  const ageCategory =
    adherent?.ageCategory ||
    (season
      ? getAgeCategoryBySeason(adherent?.date_naissance, season)
      : getCategory(adherent?.date_naissance));

  const normalizedAgeCategory = normalizeAgeCategoryForWeight(ageCategory);

  const weightCategory =
    adherent?.weightCategory ||
    getWeightCategory(adherent?.genre, normalizedAgeCategory, adherent?.poids);

  return {
    ageCategory,
    normalizedAgeCategory,
    weightCategory,
    birthYear: getBirthYear(adherent?.date_naissance),
  };
};

export const formatWeight = (weight) => {
  if (weight === null || weight === undefined || weight === "") {
    return "Non pesé(e)";
  }

  const numericWeight = Number(weight);

  if (!Number.isFinite(numericWeight) || numericWeight <= 0) {
    return "Non pesé(e)";
  }

  return `${numericWeight} kg`;
};

/* ============================================================
   TRI / OPTIONS FILTRES
   ============================================================ */

export const uniqSorted = (values = []) => {
  return Array.from(
    new Set(
      values
        .filter(
          (value) => value !== null && value !== undefined && value !== "",
        )
        .map((value) => String(value)),
    ),
  ).sort((a, b) => {
    const indexA = COMPETITION_AGE_CATEGORY_ORDER.indexOf(a);
    const indexB = COMPETITION_AGE_CATEGORY_ORDER.indexOf(b);

    if (indexA !== -1 || indexB !== -1) {
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    }

    const numberA = Number(a);
    const numberB = Number(b);

    if (!Number.isNaN(numberA) && !Number.isNaN(numberB)) {
      return numberA - numberB;
    }

    return String(a).localeCompare(String(b), "fr");
  });
};

/* ============================================================
   RÉSULTATS
   ============================================================ */

export const getResultLabel = (results) => {
  const resultats = results?.resultats || results?.results || "";
  const classement = results?.classement || results?.ranking || "";

  if (classement) return classement;
  if (resultats) return resultats;

  return "Résultat à saisir";
};
